import axios from "axios";
import { useContext } from "react";
import { useAppConfig } from "../../../../context/AppConfigContext";
import { GlobalConfigContext } from "../../../../context/GlobalConfigContext";
import { getFile } from "../../../jsonForms/fileUpload/fileStore";
import { sampleText } from "../../partnerCodeGroupSummary/sampleFile";

export function usePartnerCodeGroupForm() {
  const { globalState } = useContext(GlobalConfigContext);
  const { programName } = useAppConfig();
  const accessToken = JSON.parse(localStorage.getItem("okta-token-storage"))
    ?.accessToken?.accessToken;
  const programId =
    globalState?.programConfig?.id || "018a2bd5-fdc2-77ce-9367-7d700186d5ae";
  const partnerId = localStorage.getItem("partnerId");

  const handleError = (message, error) => {
    console.error(message, error);
    return {
      status: false,
      message: message,
    };
  };
  const getAccessToken = () => accessToken || localStorage.getItem("accessToken");

  const getPartnerId = () => partnerId || localStorage.getItem("partnerId");

  const handleDeleteFile = async (file) => {
    if (!file) {
      return handleError("File not found.");
    }

    const fileName = file.name?.split("/")[1] || file?.name;
    if (!fileName) {
      return handleError("Invalid file name format.");
    }

    const uploadUrl = window.REWARD_PARTNER_GROUP_FILE_MANAGER_BASE_URI;
    const endpoint = `${uploadUrl}/${programName}/${programId}/${partnerId}/${fileName}`;

    try {
      const response = await axios.delete(endpoint, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });

      if (response.status === 204) {
        return {
          status: true,
          message: "File Deleted Successfully",
        };
      } else {
        return handleError("Failed to delete file. Please try again.");
      }
    } catch (error) {
      return handleError("An error occurred while deleting the file.", error);
    }
  };

  const handleUploadFile = async (files) => {
    const formData = new FormData();
    Array.from(files).forEach((fileItem) => {
      let actualFile = null;
      if (fileItem?.file instanceof File) {
        actualFile = fileItem.file;
      } else if (fileItem instanceof File) {
        actualFile = fileItem;
      } else if (fileItem?.name) {
        actualFile = getFile(fileItem.name);
      }

      if (fileItem?.id == null && actualFile instanceof File) {
        formData.append("file", actualFile);
      }
    });

    try {
      const uploadUrl = window.REWARD_PARTNER_GROUP_FILE_MANAGER_BASE_URI;
      const endpoint = `${uploadUrl}upload?program_id=${programId}&partner_id=${getPartnerId()}`;

      const response = await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
          "Content-Type": "multipart/form-data",
          "program-id": localStorage.getItem("programId") || "",
        },
      });

      const uploadedFileData = response.data.map((currentFile) => ({
        id: currentFile.id,
        name: currentFile.name,
        size: currentFile.size,
        type: currentFile.type,
        url: currentFile.url || null,
        metadata: {
          description: currentFile.metadata.description,
          tags: currentFile.metadata.tags,
          uploadedBy: currentFile.metadata.uploaded_by,
          uploadedAt: currentFile.metadata.uploaded_at,
          containerName: currentFile.metadata.container_name,
        },
      }));

      return {
        status: true,
        data: uploadedFileData,
      };
    } catch (error) {
      const errMsg =
        error?.response?.data?.extension?.issues?.[0]?.detail ||
        error?.response?.data?.extension?.issues?.detail ||
        "Something went wrong";

      return handleError("Upload error: " + errMsg, error);
    }
  };

  const handleFileDownload = async (
    fileName,
    containerName = "dtc-code-group-upload"
  ) => {
    try {
      const uploadUrl = window.REWARD_PARTNER_GROUP_FILE_MANAGER_BASE_URI;
      const endpoint = `${uploadUrl}download?program_id=${programId}&partner_id=009b0d6d-d62e-46c1-9ae1-9250e5261c08&container_name=${containerName}&file_name=${fileName}`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
          "program-id": localStorage.getItem("programId") || "",
        },
        responseType: "blob", // Important for file downloads
      });
      // Create a download link for the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return {
        status: true,
        message: "File downloaded successfully",
      };
    } catch (error) {
      return handleError("An error occurred while downloading the file.", error);
    }
  };

  const handleUploadFileCall = async (files, codeGroupId) => {
    try {
      const filesToUpload = [];
      Array.from(files).forEach((fileItem) => {
        let actualFile = null;
        if (fileItem?.file instanceof File) {
          actualFile = fileItem.file;
        } else if (fileItem instanceof File) {
          actualFile = fileItem;
        } else if (fileItem?.name) {
          actualFile = getFile(fileItem.name);
        }

        if (fileItem?.id == null && actualFile instanceof File) {
          filesToUpload.push(actualFile);
        }
      });

      if (filesToUpload.length === 0) {
        return { status: true, data: [] };
      }

      const formData = new FormData();
      filesToUpload.forEach((file) => {
        formData.append("file", file);
      });

      const uploadUrl = window.REWARD_PARTNER_GROUP_FILE_MANAGER_BASE_URI;
      const endpoint = `${uploadUrl}upload?program_id=${programId}&partner_id=009b0d6d-d62e-46c1-9ae1-9250e5261c08&container_name=dtc-code-group-upload&code_group_id=${codeGroupId}`;

      const response = await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
          "Content-Type": "multipart/form-data",
          "program-id": programId,
        },
      });

      const uploadedFileData = response.data.map((currentFile) => ({
        id: currentFile.id,
        name: currentFile.name,
        size: currentFile.size,
        type: currentFile.type,
        url: currentFile.url || null,
        metadata: {
          description: currentFile.metadata.description,
          tags: currentFile.metadata.tags,
          uploadedBy: currentFile.metadata.uploaded_by,
          uploadedAt: currentFile.metadata.uploaded_at,
          containerName: currentFile.metadata["dtc-code-group-upload"],
        },
      }));
      return {
        status: true,
        data: uploadedFileData,
      };
    } catch (error) {
      const errMsg =
        error?.response?.data?.extension?.issues?.[0]?.detail ||
        error?.response?.data?.extension?.issues?.detail ||
        "Something went wrong";
      return handleError("Upload error: " + errMsg, error);
    }
  };

  const validateUploadFile = async (files) => {
    try {
      const filesToValidate = [];
      Array.from(files).forEach((fileItem) => {
        let actualFile = null;
        if (fileItem?.file instanceof File) {
          actualFile = fileItem.file;
        } else if (fileItem instanceof File) {
          actualFile = fileItem;
        } else if (fileItem?.name) {
          actualFile = getFile(fileItem.name);
        }

        if (fileItem?.id == null && actualFile instanceof File) {
          filesToValidate.push(actualFile);
        }
      });

      if (filesToValidate.length === 0) {
        return { data: { is_valid: true } };
      }

      const formData = new FormData();
      filesToValidate.forEach((file) => {
        formData.append("file", file);
      });

      const validateFileUrl = window.REWARD_PARTNER_GROUP_FILE_VALIDATE_BASE_URI;
      const endpoint = `${validateFileUrl}?container_name=dtc-code-group-upload`;

      const response = await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
          "Content-Type": "multipart/form-data",
          "program-id": programId,
        },
      });
      return response;
    } catch (error) {
      return error?.response?.data?.extension;
    }
  };

  const handleDownloadFile = async (
    fileType = "text/plain",
    downloadFileName = "sampleFile.txt",
    type = "CODEGROUP"
  ) => {
    try {
      const blob = new Blob([sampleText(type)], { type: fileType });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return null;
    } catch (error) {
      return `There was an error downloading the file: ${error}`;
    }
  };

  return {
    handleDeleteFile,
    handleUploadFile,
    handleDownloadFile,
    validateUploadFile,
    handleUploadFileCall,
    handleFileDownload,
  };
}
